import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import LocationSearch from '@/components/LocationSearch';
import ImageUpload from '@/components/ImageUpload';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Restaurant {
  id: string;
  name: string;
  description: string | null;
  address: string;
  phone: string | null;
  image_url: string | null;
  avg_prep_time_min: number | null;
  avg_prep_time_max: number | null;
}

interface BusinessSettingsProps {
  restaurantId: string;
  onUpdate: () => void;
  businessType?: string;
}

const BusinessSettings = ({ restaurantId, onUpdate, businessType = 'restaurant' }: BusinessSettingsProps) => {
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    phone: '',
    image_url: '',
    avg_prep_time_min: 20,
    avg_prep_time_max: 30
  });

  useEffect(() => {
    fetchRestaurantData();
  }, [restaurantId]);

  const fetchRestaurantData = async () => {
    try {
      const { data, error } = await supabase
        .from('restaurants')
        .select('*')
        .eq('id', restaurantId)
        .single();

      if (error) throw error;

      setRestaurant(data);
      setFormData({
        name: data.name || '',
        description: data.description || '',
        address: data.address || '',
        phone: data.phone || '',
        image_url: data.image_url || '',
        avg_prep_time_min: data.avg_prep_time_min || 20,
        avg_prep_time_max: data.avg_prep_time_max || 30
      });
    } catch (error) {
      console.error('Error fetching restaurant data:', error);
      toast({
        title: "Error",
        description: "Failed to load restaurant data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationSelect = (location: { address: string; city: string }) => {
    setFormData(prev => ({ ...prev, address: location.address }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('restaurants')
        .update({
          name: formData.name,
          description: formData.description || null,
          address: formData.address,
          phone: formData.phone || null,
          image_url: formData.image_url || null,
          avg_prep_time_min: formData.avg_prep_time_min,
          avg_prep_time_max: formData.avg_prep_time_max
        })
        .eq('id', restaurantId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Restaurant settings updated successfully",
      });

      onUpdate();
    } catch (error) {
      console.error('Error saving restaurant data:', error);
      toast({
        title: "Error",
        description: "Failed to save restaurant settings",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteBusiness = async () => {
    setDeleting(true);
    try {
      // Delete in sequence to handle foreign key constraints
      
      // 1. Delete all menu items for this restaurant
      const { error: menuItemsError } = await supabase
        .from('menu_items')
        .delete()
        .eq('restaurant_id', restaurantId);

      if (menuItemsError) throw menuItemsError;

      // 2. Delete all menu categories for this restaurant
      const { error: categoriesError } = await supabase
        .from('menu_categories')
        .delete()
        .eq('restaurant_id', restaurantId);

      if (categoriesError) throw categoriesError;

      // 3. Delete the restaurant
      const { error: restaurantError } = await supabase
        .from('restaurants')
        .delete()
        .eq('id', restaurantId);

      if (restaurantError) throw restaurantError;

      toast({
        title: "Business Deleted",
        description: "Your business has been permanently deleted from the platform",
      });

      // Navigate back to home
      navigate('/home');
    } catch (error) {
      console.error('Error deleting business:', error);
      toast({
        title: "Error",
        description: "Failed to delete business. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const isMarketplace = businessType === 'marketplace';
  const entityLabel = isMarketplace ? 'Store' : 'Restaurant';

  return (
    <div className="space-y-6">
      {/* Business Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>{entityLabel} Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ImageUpload
            bucket="restaurant-logos"
            currentImageUrl={formData.image_url}
            onImageUploaded={(url) => handleInputChange('image_url', url)}
            onImageRemoved={() => handleInputChange('image_url', '')}
            label={`${entityLabel} Logo`}
            maxSizeMB={10}
          />

          <div>
            <Label htmlFor="restaurantName">{entityLabel} Name</Label>
            <Input
              id="restaurantName"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={`Enter ${entityLabel.toLowerCase()} name`}
            />
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder={`Tell customers about your ${entityLabel.toLowerCase()}`}
              rows={4}
            />
          </div>

          <div>
            <Label htmlFor="address">Address</Label>
            <LocationSearch
              onLocationSelect={handleLocationSelect}
              placeholder={`Search for your ${entityLabel.toLowerCase()} location in Gambia...`}
              value={formData.address}
            />
          </div>

          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="Enter phone number"
            />
          </div>

          <div>
            <Label>Average Preparation Time (minutes)</Label>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex-1">
                <Label htmlFor="prepTimeMin" className="text-sm text-gray-600">Minimum</Label>
                <Input
                  id="prepTimeMin"
                  type="number"
                  min="5"
                  max="120"
                  value={formData.avg_prep_time_min}
                  onChange={(e) => handleInputChange('avg_prep_time_min', parseInt(e.target.value) || 20)}
                  placeholder="20"
                />
              </div>
              <div className="flex items-center justify-center pt-6">
                <span className="text-gray-500">to</span>
              </div>
              <div className="flex-1">
                <Label htmlFor="prepTimeMax" className="text-sm text-gray-600">Maximum</Label>
                <Input
                  id="prepTimeMax"
                  type="number"
                  min="5"
                  max="120"
                  value={formData.avg_prep_time_max}
                  onChange={(e) => handleInputChange('avg_prep_time_max', parseInt(e.target.value) || 30)}
                  placeholder="30"
                />
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              This will be displayed to customers as "{formData.avg_prep_time_min}-{formData.avg_prep_time_max} min"
            </p>
          </div>

          <Button 
            onClick={handleSave} 
            disabled={saving || !formData.name || !formData.address}
            className="w-full"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </CardContent>
      </Card>

      {/* Restaurant Hours Notice */}
      <Card>
        <CardHeader>
          <CardTitle>{entityLabel} Hours</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold mb-2 text-blue-800">Important Notice</h3>
              <p className="text-sm text-blue-700">
                Your {entityLabel.toLowerCase()}'s availability is controlled by the Business Hours you set in the "Business Hours" section. 
                Make sure to configure your operating hours there to let customers know when you're open for orders.
              </p>
            </div>
            
            <p className="text-sm text-gray-600">
              Customers will only be able to place orders during your configured business hours. 
              You can update your hours anytime from the Business Hours management page.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="text-red-700">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h3 className="font-semibold mb-2 text-red-800">Delete Business</h3>
              <p className="text-sm text-red-700 mb-4">
                This action will permanently delete your business, all menu items, and remove your restaurant from the platform. 
                This action cannot be undone.
              </p>
              
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="flex items-center gap-2">
                    <Trash2 className="h-4 w-4" />
                    Delete Business Permanently
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete your business account, 
                      all menu items, and remove your restaurant from the platform. Customers will no longer 
                      be able to see or order from your restaurant.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDeleteBusiness}
                      disabled={deleting}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      {deleting ? 'Deleting...' : 'Yes, Delete Permanently'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BusinessSettings;
